import {
  createConversation,
  submitDataAccessForm,
  updateConversation,
} from 'api/v2/AI';
import { useAiMetadata } from 'contexts/AIMetadataProvider';
import { last } from 'lodash';
import type { FC } from 'react';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from 'react-query';
import { useLocation } from 'react-router';
import ReactSpeechRecognition from 'react-speech-recognition';
import {
  AIAgentEnum,
  AIRoleEnum,
  ConversationMetadata,
  type AIUsage,
  type ConversationHistory,
  type ConversationMessage,
  type ConversationQuery,
} from 'types/ai.types';
import { AICacheKeys } from 'types/cacheKeys.types';
import { logAmplitude } from 'utils/amplitude';
import {
  WEB_AIASSISTANT_NEWCHAT,
  WEB_AIASSISTANT_USERSENTMESSAGE,
} from 'utils/amplitude/events/ai.amplitude';
import { ONE_SECOND } from 'utils/time';
import { AIChatContextValue, AIChatProviderProps } from './AIChat.types';
import {
  useAIChatAttachment,
  useConversationMetadata,
  useConversationQuery,
  useGetGratificationById,
  useUsageQuery,
} from './hooks';
import { scrollToBottom } from './utils';

export const AIChatContext = createContext<AIChatContextValue>(
  {} as AIChatContextValue,
);

export const AIChatProvider: FC<AIChatProviderProps> = ({
  children,
  aiAgent = AIAgentEnum.default,
  allowFileMetadata = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isAutoPrompt, setIsAutoPrompt] = useState(false);
  const [message, setMessage] = useState('');
  const [currentTipId, setCurrentTipId] = useState('');
  const [streamedData, setStreamedData] = useState('');
  const [conversationId, setConversationId] = useState('');
  const [conversation, setConversation] = useState<ConversationHistory | null>(
    null,
  );

  const { metadata, setMetadata } = useAiMetadata();

  const location = useLocation();

  const { data: gratification } = useGetGratificationById(currentTipId);
  const abortController = useRef(new AbortController());

  const { refetch: refetchUsage, data: usage = {} as AIUsage } = useUsageQuery(
    conversationId,
    conversation,
    message,
  );

  const { metadataItems, refetchMetadata } = useConversationMetadata(
    conversationId,
    metadata,
  );

  const metadataItemsMap = useMemo(() => {
    if (!metadataItems) return {};

    return metadataItems.reduce((acc, item) => {
      if (item._id) acc[item._id] = item;
      return acc;
    }, {} as Record<string, ConversationMetadata>);
  }, [metadataItems]);

  const queryClient = useQueryClient();
  const attachmentApi = useAIChatAttachment(allowFileMetadata);

  const { refetch: refetchConversation } = useConversationQuery(
    conversationId,
    (data) => {
      if (data?.status === 'processed' && conversationId) {
        refetchUsage();
      }
      scrollToBottom();
      setConversation(data);
      setConversationId(data?._id || '');
      attachmentApi.remove();
    },
  );

  useEffect(() => {
    setCurrentTipId('');
    setConversation(null);
  }, [location.pathname]);

  const onDownloadProgress = (progress: any): void => {
    const chunks = progress.currentTarget.response?.split('\n') as string[];
    chunks.pop();
    if (!chunks?.[0]) return;

    const lastLine = last(chunks);

    const parsedFirstChunk = JSON.parse(chunks[0]);
    const convId = parsedFirstChunk?.conversationId;
    if (conversationId !== convId) {
      setConversationId(convId);
    }
    setStreamedData(
      chunks.map((chuck: string) => JSON.parse(chuck)?.content).join(''),
    );
    if (lastLine?.includes('finish_reason')) {
      scrollToBottom();
      setStreamedData('');
      refetchConversation();
    }
  };

  const parseMetadata = () => {
    if (metadata) return metadata;
    if (currentTipId && gratification) {
      return {
        documentId: currentTipId,
        documentType: 'gratifications',
        content: gratification?.description || gratification?.title,
      };
    }
    return undefined;
  };

  const mutateConversation = (
    conversationMessage: Omit<ConversationMessage, '_id'>,
    id?: string,
  ) => {
    setStreamedData('');
    logAmplitude(WEB_AIASSISTANT_USERSENTMESSAGE);
    ReactSpeechRecognition?.stopListening?.();
    abortController.current = new AbortController();
    setIsLoading(true);
    setMessage('');

    (id ? updateConversation : createConversation)(
      {
        id,
        message: conversationMessage,
        ai_agent: aiAgent !== AIAgentEnum.default ? aiAgent : undefined,
        metadata: parseMetadata(),
      } as ConversationQuery,
      {
        onDownloadProgress,
        responseType: 'stream',
        signal: abortController.current.signal,
        timeout: 180 * ONE_SECOND,
      },
    )
      .then(() => {
        if (!id) {
          queryClient?.invalidateQueries(AICacheKeys.ConversationsList);
        }
        refetchConversation();
        refetchMetadata();
        setIsAutoPrompt(false);
        scrollToBottom();

        if (
          attachmentApi.attachment?.type === 'pdf' ||
          attachmentApi.attachment?.type === 'image'
        ) {
          attachmentApi.remove();
          setMetadata(null, attachmentApi.attachment?.type);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const disabledSend =
    isLoading || usage?.reachedMaxCount || usage?.messageLengthExceeded;

  const handleSend = (suggestedMessage?: string) => {
    if (disabledSend) return;

    const conversationMessage: Omit<ConversationMessage, '_id'> = {
      role: AIRoleEnum.user,
      content: suggestedMessage || message,
    };

    if (
      attachmentApi.attachment &&
      (metadata?.documentType === 'image' || metadata?.documentType === 'pdf')
    ) {
      conversationMessage.metadataId = metadata?._id;
    }

    const newConversation = suggestedMessage
      ? {
          messages: [{ ...conversationMessage, _id: 'tempId' }],
          status: 'processing',
          user_message_count: 1,
        }
      : {
          ...(conversation || {}),
          messages: [
            ...(conversation?.messages || []),
            { ...conversationMessage, _id: 'tempId' },
          ],
          status: 'processing',
          user_message_count: 1,
        };

    setConversation(newConversation as ConversationHistory);
    attachmentApi.remove();

    scrollToBottom();

    return mutateConversation(
      conversationMessage,
      suggestedMessage ? undefined : conversation?._id,
    );
  };

  const handleRegenerateMessage = (
    messageId: string,
    action: 'regenerate' | 'allow_access',
  ) => {
    if (!conversation?._id) return;

    setIsLoading(true);
    return submitDataAccessForm(
      conversation._id,
      messageId,
      { action },
      {
        onDownloadProgress,
        responseType: 'stream',
        signal: abortController.current.signal,
        timeout: 180 * ONE_SECOND,
      },
    ).finally(() => setIsLoading(false));
  };

  const handleNewChat = () => {
    setCurrentTipId('');
    abortController.current.abort();
    setConversation(null);
    setConversationId('');
    document.getElementById('ai-assistant-textarea')?.focus();
    logAmplitude(WEB_AIASSISTANT_NEWCHAT);
    attachmentApi.remove();
  };

  return (
    <AIChatContext.Provider
      value={{
        aiAgent,
        conversation,
        conversationId,
        currentTipId,
        isAutoPrompt,
        isLoading,
        message,
        streamedData,
        usage,
        attachmentApi,
        metadataItemsMap,
        handleNewChat,
        handleSend,
        handleRegenerateMessage,
        refetchConversation,
        setConversation,
        setConversationId,
        setCurrentTipId,
        setIsAutoPrompt,
        setMessage,
      }}
    >
      {children}
      {attachmentApi.enabled && (
        <input {...attachmentApi.dropzoneProps.input()} />
      )}
    </AIChatContext.Provider>
  );
};

export const useAIChatContext = (): AIChatContextValue => {
  const context = useContext(AIChatContext);
  if (!context) {
    throw new Error('useAIChatContext must be used within a AIChatProvider');
  }
  return context;
};
